/**
 * StandKrokiCizimi.jsx — standın krokisi, SALT-OKUNUR (backlog #710).
 *
 * ESKİSİ: şirket tarafındaki stand sayfaları (FirmaStandDetailPage'in KrokiShape'i: "Standı Gör", dört köşe "göz" düğmesinin ve Kuşbakışı'nın altındaki
 * kroki) ve yönetici kopyası (StandOverviewDetailPage'in KrokiGrid'i) her stand için aynı sabit L şeklini çiziyordu. ŞİMDİ: standın çizimi FDS-Backend'e
 * itilmişse (standKroki.cizimKonturu) onun GERÇEK ana hattı çizilir; çizilmemişse eski L şekli, "örnek şekil" diye işaretlenerek (yanıltmasın) çizilir.
 *
 * Üç hâl, tek karelik kutu (size × size): çağıranların köşe düğmeleri ve rozetleri bu kutuya göre konumlanır, bu yüzden kutu şekle göre DEĞİŞMEZ.
 *   - kontur var      → gerçek ana hat, kutuya ortalanır (kare bir görünüm kutusu; en-boy oranı korunur).
 *   - yukleniyor      → yalnızca ızgara (önce L sonra gerçek şeklin sıçraması olmasın).
 *   - ikisi de yok    → eski sabit L + "Örnek şekil" notu.
 *
 * Elektrik çıkış noktası (point): {x,y} 0-1. Gerçek ana hatta, TÜM şekillerin sınır kutusuna göre (#495 / CikisNoktasiPinner'ın yazdığı anlam); L'de eski
 * anlamı (L kutusunun 0-1'i) aynen.
 */
import React, { useId } from "react";
import { useTranslation } from "react-i18next";
import { normaldenKrokiye } from "./standKroki";

// CikisNoktasiPinner.jsx'teki KROKI_POLYGON ile AYNI sabit L şekli (viewBox 0 0 120 120).
const ORNEK_L = "10,10 65,10 65,65 110,65 110,110 10,110";
const RENK = { cizgi: "#178f8f", izgara: "#d7dfe3", yazi: "#6c757d", pin: "#dc3545" };

/** Gerçek ana hattı çevreleyen KARE görünüm (kenarı, uzun kenarın 1.24 katı: etrafta nefes payı). */
export function kareGorunum(kontur) {
  const b = kontur.bounds;
  const yan = Math.max(b.w, b.h) * 1.24;
  return { x: (b.minX + b.maxX) / 2 - yan / 2, y: (b.minY + b.maxY) / 2 - yan / 2, w: yan, h: yan };
}

/**
 * @param {object|null} kontur     standKroki.cizimKonturu() sonucu; null = gerçek ana hat yok
 * @param {boolean}     yukleniyor ana hat hâlâ isteniyor: şekil çizilmez
 * @param {number}      size       px (kare)
 * @param {{x:number,y:number}|null} point 0-1 işaret (Elektrik çıkış noktası)
 */
export default function StandKrokiCizimi({ kontur = null, yukleniyor = false, size = 160, point = null }) {
  const { t } = useTranslation("portal");
  const gridId = useId();
  const gercek = Boolean(kontur);
  const vb = gercek ? kareGorunum(kontur) : { x: 0, y: 0, w: 120, h: 120 };
  const adim = gercek ? vb.w / 8 : 15;
  const pin = point ? (gercek ? normaldenKrokiye(kontur, point) : { x: point.x * 120, y: point.y * 120 }) : null;
  const pinR = gercek ? vb.w * 0.04 : 5;

  return (
    <svg
      viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`} width={size} height={size}
      role="img" aria-label={gercek ? t("standDetay.krokiGercekSekil") : t("standDetay.krokiOrnekSekil")}
    >
      <defs>
        <pattern id={gridId} x={vb.x} y={vb.y} width={adim} height={adim} patternUnits="userSpaceOnUse">
          <path d={`M ${adim} 0 L 0 0 0 ${adim}`} fill="none" stroke={RENK.izgara} strokeWidth="1" vectorEffect="non-scaling-stroke" />
        </pattern>
      </defs>
      <rect x={vb.x} y={vb.y} width={vb.w} height={vb.h} fill={`url(#${gridId})`} />

      {gercek && kontur.shapes.map((s) => (
        <polygon
          key={s.shapeId} points={s.points.map((p) => `${p.x},${p.y}`).join(" ")}
          fill={RENK.cizgi} fillOpacity="0.18" stroke={RENK.cizgi} strokeWidth="1.5" strokeLinejoin="round" vectorEffect="non-scaling-stroke"
        />
      ))}

      {!gercek && !yukleniyor && (
        <>
          <polygon points={ORNEK_L} fill={RENK.cizgi} fillOpacity="0.18" stroke={RENK.cizgi} strokeWidth="1.5" />
          {/* L bir ÖRNEKTİR (stand çizilmemiş ya da çizimi okunamıyor): şirketin kendi standının şekli sanılmasın diye işaretlenir. */}
          <text x="60" y="118" fontSize="7" textAnchor="middle" fill={RENK.yazi}>{t("standDetay.krokiOrnekSekilNotu")}</text>
        </>
      )}

      {pin && (
        <circle cx={pin.x} cy={pin.y} r={pinR} fill={RENK.pin} stroke="#fff" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      )}
    </svg>
  );
}
